import { setNetworkActivityObserver } from '@eureca/candidate-api-sdk/client';
import type { QueryClient } from '@tanstack/react-query';
import { isNetworkFailure } from './is-network-failure';
import { offlineStore } from './offline-store';

/**
 * EU-711 — pluga o detector de offline no transporte do app.
 *
 * O `fetchClient` do `candidate-api-sdk` é o único ponto por onde passam TODAS as
 * superfícies (listagem, navegação, perfil, conclusão de etapa, upload de
 * currículo). Observar ali é o que entrega FR-002 — "a mesma tela em todo o App
 * Candidato" — sem editar cada tela, e é o que faz a próxima tela nascer coberta.
 *
 * O observador do SDK é opt-in (`null` por default): só o App Candidato instala,
 * os outros consumidores do package não mudam de comportamento.
 */
export function installOfflineDetector(queryClient: QueryClient): void {
  // OFF-012 — "Tentar novamente" nas telas que não registram uma ação própria
  // (listagem, navegação, perfil): re-busca o que está ativo em tela. Injetado
  // aqui para o store não precisar conhecer o react-query.
  offlineStore.setReloadActiveData(async () => {
    await queryClient.refetchQueries({ type: 'active' });
    // `refetchQueries` RESOLVE mesmo quando todas as queries falharam — ele
    // espera o refetch terminar, não dar certo. Sem reler o cache, a re-tentativa
    // parecia bem-sucedida com a rede ainda caída e o candidato caía numa árvore
    // que não carregou (OFF-004). Relançar devolve ele à tela.
    const aindaSemConexao = queryClient
      .getQueryCache()
      .findAll({ type: 'active' })
      .some((query) => isNetworkFailure(query.state.error));
    if (aindaSemConexao) {
      throw Object.assign(new Error('Network error'), { status: 0 });
    }
  });

  // No-op em produção: `start-before-router.ts` (primeiro import do router) já
  // ligou o store, para o `popstate` do gate ficar à frente do do router
  // (EU-762, QA 01/10). Fica aqui para o boot não depender daquele import.
  offlineStore.start();

  setNetworkActivityObserver((event, requestId, meta) => {
    if (event === 'start') {
      offlineStore.reportRequestStart(requestId, { watchdog: meta.watchdog });
      return;
    }
    if (event === 'success') {
      offlineStore.reportRequestSuccess(requestId);
      return;
    }
    if (event === 'settled') {
      offlineStore.reportRequestSettled(requestId);
      return;
    }
    // DEV-95 (caso A) — o meta vai TAMBÉM no caminho de falha. A linha do `start`
    // acima isenta o upload de currículo de propósito (`watchdog: false`); sem
    // repassar aqui, a isenção era anulada na falha e o `uploadWithRetry` abria
    // "Você está sem internet" no meio do envio, com a conexão funcionando.
    offlineStore.reportNetworkError(requestId, { watchdog: meta.watchdog });
  });
}
